import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import BirthInfoForm from '@/components/BirthInfoForm';
import ChartDisplay from '@/components/ChartDisplay';
import InterpretationPanel from '@/components/InterpretationPanel';
import DetailedInterpretationPanel from '@/components/DetailedInterpretationPanel';
import { BirthInfo, ZiweiChart, calculateZiweiChart } from '@/lib/ziwei-calculator';
import { Stars, RotateCcw, Download } from 'lucide-react';

const Index = () => {
  const [chart, setChart] = useState<ZiweiChart | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleCalculateChart = async (birthInfo: BirthInfo) => {
    setIsCalculating(true);
    
    // 模擬計算過程
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    try {
      const newChart = calculateZiweiChart(birthInfo);
      setChart(newChart);
    } catch (error) {
      console.error('計算命盤時發生錯誤:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  const handleReset = () => {
    setChart(null);
  };

  const handleExport = () => {
    if (!chart) return;
    
    // 創建導出數據
    const exportData = {
      birthInfo: chart.birthInfo,
      palaces: chart.palaces,
      lifeStage: chart.lifeStage,
      bodyPalace: chart.bodyPalace,
      exportTime: new Date().toISOString()
    };
    
    // 下載JSON文件
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `紫微斗數命盤_${chart.birthInfo.year}${chart.birthInfo.month}${chart.birthInfo.day}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50">
      {/* 頭部 */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-purple-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Stars className="w-8 h-8 text-purple-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">紫微斗數排盤系統</h1>
                <p className="text-sm text-gray-600">專業命理分析工具</p>
              </div>
            </div>
            
            {chart && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleExport} className="flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  導出命盤
                </Button>
                <Button variant="outline" onClick={handleReset} className="flex items-center gap-2">
                  <RotateCcw className="w-4 h-4" />
                  重新排盤
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {!chart ? (
          /* 輸入表單 */
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">開始您的命理之旅</h2>
              <p className="text-lg text-gray-600 mb-6">
                請輸入您的出生資訊，我們將為您排出專業的紫微斗數命盤
              </p>
              
              {/* 特色說明 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <Card className="border-purple-200">
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Stars className="w-6 h-6 text-purple-600" />
                    </div>
                    <h3 className="font-semibold mb-1">專業排盤</h3>
                    <p className="text-sm text-gray-600">基於傳統紫微斗數理論，精確計算星曜位置</p>
                  </CardContent>
                </Card>
                
                <Card className="border-blue-200">
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <RotateCcw className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="font-semibold mb-1">靈活選項</h3>
                    <p className="text-sm text-gray-600">支援沒有確切出生時間的用戶進行排盤</p>
                  </CardContent>
                </Card>
                
                <Card className="border-green-200">
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Download className="w-6 h-6 text-green-600" />
                    </div>
                    <h3 className="font-semibold mb-1">詳細解讀</h3>
                    <p className="text-sm text-gray-600">提供全面的命盤分析和宮位解讀</p>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            <BirthInfoForm onSubmit={handleCalculateChart} />
            
            {isCalculating && (
              <div className="text-center mt-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                  正在計算命盤，請稍候...
                </div>
              </div>
            )}
          </div>
        ) : (
          /* 命盤結果 */
          <div className="max-w-7xl mx-auto">
            <Tabs defaultValue="chart" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-6">
                <TabsTrigger value="chart">命盤圖</TabsTrigger>
                <TabsTrigger value="interpretation">基本解讀</TabsTrigger>
                <TabsTrigger value="detailed">詳細運程</TabsTrigger>
              </TabsList>
              
              <TabsContent value="chart" className="space-y-6">
                {/* 基本資訊 */}
                <Card>
                  <CardHeader>
                    <CardTitle>基本資訊</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">性別：</span>
                        <span className="font-medium">{chart.birthInfo.gender === 'male' ? '男' : '女'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">出生日期：</span>
                        <span className="font-medium">
                          {chart.birthInfo.year}年{chart.birthInfo.month}月{chart.birthInfo.day}日
                          {chart.birthInfo.isLunar ? ' (農曆)' : ' (公曆)'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">出生時間：</span>
                        <span className="font-medium">
                          {chart.birthInfo.hasTime 
                            ? `${chart.birthInfo.hour?.toString().padStart(2, '0')}:${chart.birthInfo.minute?.toString().padStart(2, '0')}`
                            : '未提供 (使用午時)'
                          }
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">當前大限：</span>
                        <span className="font-medium">{chart.lifeStage}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* 命盤圖 */}
                <ChartDisplay palaces={chart.palaces} bodyPalace={chart.bodyPalace} />
              </TabsContent>
              
              <TabsContent value="interpretation">
                <InterpretationPanel chart={chart} />
              </TabsContent>
              
              <TabsContent value="detailed">
                <DetailedInterpretationPanel chart={chart} />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </main>
      
      {/* 頁腳 */}
      <footer className="bg-white/80 backdrop-blur-sm border-t border-purple-200 mt-16">
        <div className="container mx-auto px-4 py-6">
          <div className="text-center text-sm text-gray-600">
            <p>紫微斗數排盤系統 - 僅供參考，不可盡信</p>
            <p className="mt-1">建議搭配專業命理師諮詢以獲得更準確的解讀</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
